import type { RaRecord } from "react-admin";

type SadaratNameLike = { name?: string | null } | null | undefined;

export type RazaEventSadaratDisplayInput = {
  needSadaratMale?: boolean | null;
  needSadaratFemale?: boolean | null;
  sadaratMale?: SadaratNameLike;
  sadaratFemale?: SadaratNameLike;
  /** Legacy single-slot fields */
  needSadarat?: boolean | null;
  sadarat?: SadaratNameLike;
};

/** List/show rows from react-admin are `RaRecord`; accept both. */
export type RazaEventSadaratDisplayRow = RazaEventSadaratDisplayInput | RaRecord;

function asDisplayInput(row: RazaEventSadaratDisplayRow): RazaEventSadaratDisplayInput {
  return row as RazaEventSadaratDisplayInput;
}

function trimName(s: SadaratNameLike): string {
  return (s?.name ?? "").trim();
}

/** True when either gender slot (or legacy needSadarat) requires sadarat. */
export function eventNeedsAnySadarat(ev: RazaEventSadaratDisplayRow): boolean {
  const row = asDisplayInput(ev);
  if (row.needSadaratMale !== undefined || row.needSadaratFemale !== undefined) {
    return Boolean(row.needSadaratMale) || Boolean(row.needSadaratFemale);
  }
  return row.needSadarat !== false;
}

/**
 * List / calendar / show label: `Male: X · Female: Y` with missing flags for needed empty slots.
 * Legacy single `sadarat` falls back to name only.
 */
export function formatRazaEventSadaratListLabel(
  row: RazaEventSadaratDisplayRow,
  options?: { empty?: string }
): string {
  const input = asDisplayInput(row);
  const empty = options?.empty ?? "—";
  const usesDual =
    input.needSadaratMale !== undefined ||
    input.needSadaratFemale !== undefined ||
    input.sadaratMale != null ||
    input.sadaratFemale != null;

  if (usesDual) {
    const parts: string[] = [];
    if (input.needSadaratMale) {
      const n = trimName(input.sadaratMale);
      parts.push(n ? `Male: ${n}` : "Male: missing");
    }
    if (input.needSadaratFemale) {
      const n = trimName(input.sadaratFemale);
      parts.push(n ? `Female: ${n}` : "Female: missing");
    }
    return parts.length > 0 ? parts.join(" · ") : empty;
  }

  if (input.needSadarat === false) return empty;
  const legacy = trimName(input.sadarat);
  return legacy || empty;
}
